import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import useUiuxStore from "src/store/useUiuxStore";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate, formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";

import { GridMonthCalendar } from "src/components/common/form/GridMonthCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { changeWarehouseColumn, CommonCodeColumn, getCommonCodeColumnOptions, getWarehouseColumnOptions, WarehouseColumn } from "src/utils/grid/GridColumnContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { useWMS2020_R01Store } from "./Store_WMS2020_R01";
import { formatYear } from "react-calendar/dist/shared/dateFormatter";
import { defaultWMS2020_R01Dto, WMS2020_R01Dto } from "./Dto_WMS2020_R01";
import { showWarning } from "src/utils/toast/toastUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */
 
interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<WMS2020_R01Dto> | null>;
}

const COLUMN_STATE_KEY = 'WMS2020_R01-detail-grid-column-state';

export const WMS2020_R01DetailTable = (props: Props) => {
	const { clickable, setClickable, gridRef } = props;

	const {
		WMS2020_R01DetailList, setWMS2020_R01DetailList,
		updateWMS2020_R01DetailList,
		deletedWMS2020_R01DetailList,
		selectedWMS2020_R01DetailList, addSelectedWMS2020_R01DetailList, removeSelectedWMS2020_R01DetailList,
		initSelectedWMS2020_R01DetailList, allSelectedWMS2020_R01DetailList,
		originalWMS2020_R01DetailList,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
		WMS2020_R01ProcessFilter,
	} = useWMS2020_R01Store();
	const { wmsCdList, warehouseCdList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: WMS2020_R01DetailList,
		defaultGridData: defaultWMS2020_R01Dto,
		selectedRow: selectedWMS2020_R01DetailList,
		originalGridData: originalWMS2020_R01DetailList,
		updateRow: (index: number, data: any) => updateWMS2020_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		logicCenter: '물류센터',
		warehouseCd: '창고코드',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		itemStatus: '재고등급',
		itemNo: '품목번호',
		lotNo: 'LOT No',
		lotQty: '재고량',
		movedLotQty: '이동량',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
	disabledCells: ['logicCenter', 'warehouseCd', 'itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'itemNo', 'lotNo', 'lotQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: ['movedLotQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	
	alignLeftCells: ['warehouseCd']
}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<WMS2020_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedWMS2020_R01DetailList,
            deletedList: deletedWMS2020_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		
        selectedWMS2020_R01DetailList, deletedWMS2020_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'movedLotQty' ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = [ 'movedLotQty' ];

	const baseColumnDefs: ColDef<WMS2020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 100,
			...CommonCodeColumn.options(wmsCdList, false, undefined, undefined),
		},
		{
			headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 100,
			// 3번째 인자로 함수 전달: params.data에서 logicCenter 값을 가져옴
			...WarehouseColumn.options(warehouseCdList, false, (params: any) => params.data?.logicCenter, undefined, undefined),
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 140,
			editable: false,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			editable: false,
		},
		{
			headerName: '재고등급', colId: 'itemStatus', field: 'itemStatus', width: 100,
			editable: false,
		},
		{
			headerName: '품목번호', colId: 'itemNo', field: 'itemNo', width: 140,
			editable: false,
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 140,
			editable: false,
		},
		{
			headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 100,
			editable: false,
		},
		{
			headerName: '이동량', colId: 'movedLotQty', field: 'movedLotQty', width: 100,
			// editable: WMS2020_R01ProcessFilter.processType === 'split' ? true : false,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		// ...IndexParameterCell(),
	], [
		deletedWMS2020_R01DetailList, WMS2020_R01DetailList, wmsCdList, warehouseCdList, WMS2020_R01ProcessFilter, createCellClassRules,
	]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

const columnDefs: ColDef<WMS2020_R01Dto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<WMS2020_R01Dto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState]);

    const handleCellChange = useCellChangeHandler({
        dataList: WMS2020_R01DetailList,
        originalDataList: originalWMS2020_R01DetailList,
        updateRow: updateWMS2020_R01DetailList,
		customBeforeUpdate: (updatedData, field, value) => {
			if(field === 'movedLotQty' && WMS2020_R01ProcessFilter.processType === 'move') {
				showWarning('이동작업 시 이동량 기입은 불가능합니다.');
				return { ...updatedData, movedLotQty: updatedData.lotQty };
			}
			return updatedData;
		},
    });

    const handleRowClick = useRowSelectionHandler({
        dataList: WMS2020_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedWMS2020_R01DetailList,
        deletedList: deletedWMS2020_R01DetailList,
        addSelected: addSelectedWMS2020_R01DetailList,
        removeSelected: removeSelectedWMS2020_R01DetailList,
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: WMS2020_R01DetailList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<WMS2020_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(WMS2020_R01DetailList, 'valueIndex', cellClickActions);
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<WMS2020_R01Dto>[] = useMemo(() => [
	], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(WMS2020_R01DetailList, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<WMS2020_R01Dto>) => {
		postSortHandler(params, WMS2020_R01DetailList, originalWMS2020_R01DetailList, setWMS2020_R01DetailList);
	}, [WMS2020_R01DetailList, originalWMS2020_R01DetailList, ]);

	const pinnedBottomRow = useMemo(() => {
		return [{
			rowIndex: '계',
			logicCenter: WMS2020_R01DetailList.length.toLocaleString() + "건",
			editable: false,
		}];
	},[WMS2020_R01DetailList.length]);

    return (
		<GridContextWrapper<WMS2020_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setWMS2020_R01DetailList}
			initSelected={initSelectedWMS2020_R01DetailList}	allSelected={allSelectedWMS2020_R01DetailList}
			defaultRowData={defaultWMS2020_R01Dto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<WMS2020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<WMS2020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedWMS2020_R01DetailList,
					deletedList: deletedWMS2020_R01DetailList
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setDetailColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
				
			/>
		</GridContextWrapper>
    )
}
